import { ApiCompetition, ApiCompetitionDetail } from "griptonite-api-client";
import groupBy from "lodash/groupBy";
import { useEffect, useRef } from "react";

import useLocalStorage from "@/hooks/useLocalStorage";

type Warning = {
  message: string;
  field?: string;
  tab: string;
};

export default function getPublishWarnings(competition?: ApiCompetition | ApiCompetitionDetail) {
  const warnings: Warning[] = [];

  if (!competition || !competition.isHidden) {
    return {
      allWarnings: warnings,
      byTab: {},
    };
  }

  if (!competition.description) {
    warnings.push({
      message: "Missing description",
      tab: "details",
    });
  }

  if (competition.media.length === 0) {
    warnings.push({
      message: "No media has been uploaded. At least one image is required to use as a cover image",
      tab: "details",
    });
  }

  if (competition.tagEntry && competition.tags.length === 0) {
    warnings.push({
      message: "You have selected tag entry, but haven't added a tag",
      tab: "details",
    });
  }

  if (!competition.stages[0].categories.length) {
    warnings.push({
      message: "No categories have been added",
      tab: "categories",
    });
  }

  if ("autoRouteGroupMode" in competition && !competition.autoRouteGroupMode) {
    warnings.push({
      message: "You need to choose a circuit mode",
      tab: "circuits",
    });
  }

  return {
    allWarnings: warnings,
    byTab: groupBy(warnings, "tab"),
  };
}

export const useHideWarningsMap = (
  competitionUuid?: string,
): Record<string, boolean | null | undefined> => {
  const localStorageValues = useLocalStorage({
    keys: [
      `competitions:${competitionUuid}:hideWarnings:categories`,
      `competitions:${competitionUuid}:hideWarnings:circuits`,
      `competitions:${competitionUuid}:hideWarnings:scoring`,
    ],
  });
  return {
    categories: localStorageValues[`competitions:${competitionUuid}:hideWarnings:categories`],
    circuits: localStorageValues[`competitions:${competitionUuid}:hideWarnings:circuits`],
    scoring: localStorageValues[`competitions:${competitionUuid}:hideWarnings:scoring`],
  } as Record<string, boolean | null | undefined>;
};

/**
 * Unhide warnings from the previous tab(s) when the component mounts.
 */
export function useUnhidePreviousTabWarningsOnMount(
  competitionUuid: string,
  previousTab: string,
  ...additionalTabs: string[]
) {
  const allTabs = [previousTab, ...additionalTabs];
  const warningKeys = allTabs.map((tab) => `competitions:${competitionUuid}:hideWarnings:${tab}`);

  const { setItem: localStorageSetItem } = useLocalStorage({
    keys: warningKeys,
  });

  const warningKeysRef = useRef(warningKeys);

  // Unhide warnings when the component mounts
  useEffect(() => {
    warningKeysRef.current.forEach((key) => {
      localStorageSetItem(key, false);
    });
  }, [competitionUuid, localStorageSetItem]);

  return localStorageSetItem;
}
